import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ProfileImage from "../profileImage";

const ChatHeader = ({ 
  title, 
  roomUsers = [], 
  roomInfo = {}, 
  type = 'private',
  onBack,
  showFilters = false
}) => {

  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // Get the other participant for private chats
  const otherParticipant = roomUsers.find(user => !user.isCurrentUser) || roomUsers.find(user => user._id !== roomInfo.currentUserId) || roomUsers[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Debug logging
  console.log("ChatHeader Debug:", {
    roomUsers,
    roomInfo,
    otherParticipant,
    title
  });

  // Handle feed navigation
  const handleViewFeed = () => {
    if (otherParticipant?._id) {
      navigate(`/feed/${otherParticipant._id}`);
    }
    setShowDropdown(false);
  };

  return (
    <div className="bg-white border-b border-gray-200">
      {/* Main Header */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          {/* Left side - Back button and title */}
          <div className="flex items-center space-x-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            
            <div 
              className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
              onClick={handleViewFeed}
              title="View Feed"
            >
              {/* User Avatar */}
              <div className="relative">
                {otherParticipant ? (
                  <ProfileImage
                    image={otherParticipant?.profilePicture}
                    user={otherParticipant}
                    alt={title}
                    className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100 hover:ring-blue-200 transition-all"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse ring-2 ring-gray-100"></div>
                )}
                
                {/* Online status indicator */}
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
              </div>

              {/* Title and subtitle */}
              <div>
                <h1 className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                  {title}
                </h1>
                <p className="text-sm text-gray-500">
                  {otherParticipant?.accountType && (
                    <span className="capitalize">
                      {otherParticipant.accountType.replace('-', ' ')}
                      {otherParticipant.accountType === 'talent-scout' && (
                        <span className="ml-1 text-yellow-500" title="Premium User">👑</span>
                      )}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Right side - Action buttons */}
          <div className="flex items-center space-x-2">
            {/* Profile/More options dropdown */}
            {otherParticipant && (
              <div className="relative" ref={dropdownRef}>
                <button 
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600"
                  aria-label="More options"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    {(otherParticipant.accountType === 'organization' || 
                      otherParticipant.accountType === 'player' || 
                      otherParticipant.accountType === 'talent-scout') && (
                      <button
                        onClick={() => {
                          navigate(`/feed/${otherParticipant._id}`);
                          setShowDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                        <span>View Feed</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Additional participants info (for group chats if needed) */}
      {roomUsers.length > 2 && (
        <div className="px-4 pb-4">
          <div className="flex items-center space-x-3 overflow-x-auto">
            {roomUsers.slice(0, 5).map((user) => (
              <div key={user._id} className="flex items-center bg-gray-50 rounded-full px-3 py-2 shadow-sm flex-shrink-0">
                <ProfileImage
                  image={user.profilePicture}
                  alt={`${user.firstName} ${user.lastName}`}
                  className="w-8 h-8 rounded-full object-cover ring-2 ring-white"
                />
                <div className="ml-2">
                  <p className="text-sm font-medium text-gray-800">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {user.accountType?.replace('-', ' ')}
                  </p>
                </div>
                {user.accountType === 'talent-scout' && (
                  <span className="ml-2 text-yellow-500" title="Premium User">
                    👑
                  </span>
                )}
              </div>
            ))}
            {roomUsers.length > 5 && (
              <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full text-sm text-gray-600">
                +{roomUsers.length - 5}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatHeader; 